/**
 * Opening the active group's key, once per group per session.
 *
 * Nothing in a group can be decrypted until this has run: file keys are sealed
 * with the group key, and the group key arrives sealed to the member's own
 * public half. So this is the first thing the gallery waits on, and the reason
 * a tile can be "waiting for the key" rather than merely loading.
 *
 * The four outcomes are genuinely different and the interface says which:
 *
 *   `ready`        — the key is open, photographs will decrypt.
 *   `locked`       — signed in, but the keyring was never opened this session,
 *                    which is what a page refresh leaves behind. Only a
 *                    sign-in can fix it, because only then is the password in
 *                    memory.
 *   `unencrypted`  — this group predates encryption and has no key at all. Its
 *                    photographs render straight from storage.
 *   `awaiting-grant`— the group *is* encrypted and this member has no copy of
 *                    the key yet. Nothing here can fix it: only somebody who
 *                    already holds the key can hand one over, which the next
 *                    member to open the group does automatically.
 *                    Told apart from `unencrypted` because they used to look
 *                    identical — a null grant either way — and the gallery
 *                    responded by rendering sealed files as plain ones and
 *                    filling the screen with red boxes.
 *   `failed`       — the sealed key is there and will not open. Rare, and worth
 *                    saying plainly rather than showing an empty gallery.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  hasGroupKey,
  isUnlocked,
  openGroupKey,
  subscribeKeyring,
} from "./keyring.js";
import { trpcClient } from "./trpc.js";

export type GroupKeyState =
  | "opening"
  | "ready"
  | "locked"
  | "unencrypted"
  | "awaiting-grant"
  | "failed";

/** Re-renders when the keyring locks or unlocks, so sign-out is not stale. */
function useKeyringUnlocked(): boolean {
  return useSyncExternalStore(subscribeKeyring, isUnlocked, () => false);
}

export function useGroupKey(groupId: string | null | undefined): GroupKeyState {
  const unlocked = useKeyringUnlocked();
  const [opened, setOpened] = useState(() =>
    groupId ? hasGroupKey(groupId) : false,
  );

  /*
   * Fetched even while the keyring is locked. The answer distinguishes "there
   * is no key" from "there is a key nobody here can open", and the interface
   * needs to tell those apart before it can say anything useful.
   */
  const grant = useQuery({
    queryKey: ["groupKey", groupId],
    enabled: Boolean(groupId),
    queryFn: () => trpcClient.groups.groupKey.query({ groupId: groupId! }),
    // The grant changes only when someone re-shares the group, which is rare
    // and always accompanied by a reload of the member list.
    staleTime: 60 * 60_000,
    retry: 1,
  });

  const [failed, setFailed] = useState(false);
  const wrapped = grant.data?.wrappedGroupKey ?? null;
  const groupEncrypted = grant.data?.groupEncrypted ?? false;

  useEffect(() => {
    if (!groupId || !wrapped || !unlocked) return;
    if (hasGroupKey(groupId)) {
      setOpened(true);
      return;
    }

    let current = true;
    setFailed(false);
    openGroupKey(groupId, wrapped)
      .then(() => {
        if (current) setOpened(true);
      })
      .catch(() => {
        if (current) setFailed(true);
      });

    return () => {
      current = false;
    };
  }, [groupId, wrapped, unlocked]);

  // Locking must take the "already open" flag with it, or a signed-out tab
  // would keep claiming the gallery is readable.
  useEffect(() => {
    if (!unlocked) setOpened(false);
  }, [unlocked]);

  if (!groupId) return "opening";
  if (grant.isPending) return "opening";
  if (!wrapped) return groupEncrypted ? "awaiting-grant" : "unencrypted";
  if (opened) return "ready";
  if (!unlocked) return "locked";
  if (failed) return "failed";
  return "opening";
}
